import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AppNotification } from '@tutor/shared';
import { Button, cn } from '@tutor/ui';
import { api } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-client';
import { formatAgo } from '../../lib/format';

interface NotificationsResponse {
  unread: number;
  items: AppNotification[];
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: queryKeys.notifications.list,
    queryFn: () => api.get<NotificationsResponse>('/api/me/notifications'),
    refetchInterval: 60_000,
  });

  const markAll = useMutation({
    mutationFn: () => api.post('/api/me/notifications/read-all'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list }),
  });

  // Close on an outside click so the panel behaves like a normal menu.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const unread = data?.unread ?? 0;

  return (
    <div className="relative" ref={containerRef}>
      <Button
        variant="ghost"
        size="sm"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="relative"
      >
        <Bell className="h-5 w-5" aria-hidden />
        {unread > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
            <p className="text-sm font-semibold text-slate-900">Notifications</p>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                className="text-xs font-medium text-brand-600 hover:text-brand-700"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {(data?.items.length ?? 0) === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-slate-500">Nothing new right now.</p>
            ) : (
              data!.items.map((item) => {
                const body = (
                  <>
                    <p className="text-sm font-medium text-slate-900">{item.title}</p>
                    {item.body && <p className="mt-0.5 text-sm text-slate-500">{item.body}</p>}
                    <p className="mt-1 text-xs text-slate-400">{formatAgo(item.createdAt)}</p>
                  </>
                );

                return (
                  <div
                    key={item.id}
                    className={cn(
                      'border-b border-slate-50 px-4 py-3 last:border-0',
                      !item.readAt && 'bg-brand-50/40',
                    )}
                  >
                    {item.href ? (
                      <Link to={item.href} onClick={() => setOpen(false)} className="block">
                        {body}
                      </Link>
                    ) : (
                      body
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
